import React, { useState, useRef, useEffect } from 'react';

const ChatPopup = ({ 
  isOpen, 
  onClose, 
  chatMessages, 
  onSendMessage, 
  playerColor,
  players 
}) => {
  const [chatInput, setChatInput] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const chatMessagesRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Auto-focus input when popup opens on desktop
  useEffect(() => {
    if (isOpen && !isMobile && chatInputRef.current) {
      setTimeout(() => chatInputRef.current.focus(), 100);
    }
  }, [isOpen, isMobile]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      onSendMessage(chatInput.trim());
      setChatInput('');
      // Keep focus on input for better UX on desktop
      if (!isMobile && chatInputRef.current) {
        chatInputRef.current.focus();
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Handle mobile keyboard
  const handleInputFocus = () => {
    if (isMobile) {
      // Scroll chat to bottom when keyboard appears
      setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 300);
    }
  };

  const isMyMessage = (message) => {
    const currentPlayer = Object.values(players).find(p => p.color === playerColor);
    return currentPlayer && message.playerId === currentPlayer.id;
  };

  if (!isOpen) return null;

  return (
    <div className="chat-popup-overlay">
      <div className="chat-popup">
        <div className="chat-header">
          <h3>Chat</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="chat-messages" ref={chatMessagesRef}>
          {chatMessages.length === 0 ? (
            <div className="no-messages">No messages yet. Start the conversation!</div>
          ) : (
            chatMessages.map((message) => (
              <div 
                key={message.id} 
                className={`chat-message ${isMyMessage(message) ? 'own-message' : ''}`}
              >
                <div className="message-header">
                  <span className="sender">
                    {isMyMessage(message) ? 'You' : message.playerName}
                  </span>
                  <span className="timestamp">
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="message-text">{message.text}</div>
              </div>
            ))
          )}
        </div>
        
        <form className="chat-input-container" onSubmit={handleSendMessage}>
          <input
            ref={chatInputRef}
            type="text"
            className="chat-input"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={handleInputFocus}
            placeholder="Type a message..."
            maxLength={200}
            autoComplete="off"
            style={{ fontSize: isMobile ? '16px' : '14px' }} // Prevents zoom on iOS
          />
          <button 
            type="submit" 
            className="send-button"
            disabled={!chatInput.trim()}
          >
            {isMobile ? '→' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPopup;
